/**
 * Route preloader utility for lazy-loaded routes
 * Preloads route components on hover/focus to improve perceived performance
 */

type RouteLoader = () => Promise<any>;

// Map of route paths to their lazy loaders
const routeLoaders: Record<string, RouteLoader> = {
  '/': () => import('../pages/Index'),
  '/logg-inn': () => import('../pages/Auth'),
  '/registrer': () => import('../pages/Register'),
  '/registrer-veiviser': () => import('../pages/auth/RegisterWizard'),
  '/bli-bedrift': () => import('../pages/onboarding/CompanyOnboarding'),
  '/bli-tilbyder': () => import('../pages/onboarding/ProviderOnboarding'),
  '/bedrift': () => import('../pages/dashboard/CompanyDashboard'),
  '/tilbyder': () => import('../pages/dashboard/ProviderDashboard'),
  '/admin': () => import('../pages/dashboard/AdminDashboard'),
  '/finn-tilbyder': () => import('../pages/Search'),
  '/hvordan-det-fungerer': () => import('../pages/HowItWorks'),
  '/for-bedrifter': () => import('../pages/ForBedrifter'),
  '/for-tilbydere': () => import('../pages/ForTilbydere'),
  '/om-oss': () => import('../pages/OmOss'),
  '/kontakt': () => import('../pages/Kontakt'),
  '/hvor-mye-kan-jeg-spare': () => import('../pages/HvorMyeKanJegSpaere'),
  '/personvern-og-vilkar': () => import('../pages/PersonvernOgVilkar'),
  '/notifications': () => import('../pages/dashboard/Notifications'),
};

// Cache to track which routes have been preloaded
const preloadedRoutes = new Set<string>();

/**
 * Preload a route by its path
 */
export const preloadRoute = (path: string): void => {
  // Normalize path by removing trailing slash and extracting base path
  const normalizedPath = path.split('?')[0].replace(/\/$/, '') || '/';
  
  // Check if already preloaded
  if (preloadedRoutes.has(normalizedPath)) {
    return;
  }

  // Get the loader for this route
  const loader = routeLoaders[normalizedPath];
  
  if (loader) {
    // Mark as being preloaded
    preloadedRoutes.add(normalizedPath);
    
    // Start loading the component
    loader().catch((error) => {
      // Remove from cache on error so it can be retried
      preloadedRoutes.delete(normalizedPath);
      console.error(`Failed to preload route: ${path}`, error);
    });
  }
};

/**
 * Preload multiple routes at once
 */
export const preloadRoutes = (paths: string[]): void => {
  paths.forEach(preloadRoute);
};
